import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { fetchMyItems } from '@/api/me';

/**
 * Suivi des objets « nouveaux » de l'inventaire (cf reflexions.txt) : tout objet
 * possédé dont l'id n'a pas encore été VU (survolé/cliqué) est « new ». L'état
 * vu est persisté en localStorage (par joueur via la clé brute — les ids
 * d'instance sont uniques en base, pas de collision inter-parties).
 *
 *  - `newCount`  : alimente le badge numérique du menu Inventaire.
 *  - `isNew(id)` : pilote le badge « New » sur chaque carte d'inventaire.
 *  - `markSeen`  : appelé au survol/clic d'un objet.
 *  - `refresh`   : recharge la liste des ids possédés (sur tick / ouverture).
 */
const LS_KEY = 'theho.inventory.seenItemIds';

function loadSeen(): Set<number> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
  } catch {
    return new Set();
  }
}

export const useInventoryNewStore = defineStore('inventory-new', () => {
  const itemIds = ref<number[]>([]);
  const seen = ref<Set<number>>(loadSeen());

  const newIds = computed(() => itemIds.value.filter((id) => !seen.value.has(id)));
  const newCount = computed(() => newIds.value.length);

  function persist(): void {
    try { localStorage.setItem(LS_KEY, JSON.stringify([...seen.value])); } catch { /* quota / privé : on ignore */ }
  }

  function isNew(id: number): boolean {
    return !seen.value.has(id);
  }

  function markSeen(id: number): void {
    if (seen.value.has(id)) return;
    seen.value.add(id);
    seen.value = new Set(seen.value); // réassignation = réactivité
    persist();
  }

  function markAllSeen(): void {
    let changed = false;
    for (const id of itemIds.value) {
      if (!seen.value.has(id)) { seen.value.add(id); changed = true; }
    }
    if (changed) { seen.value = new Set(seen.value); persist(); }
  }

  async function refresh(): Promise<void> {
    try {
      const dto = await fetchMyItems();
      itemIds.value = dto.items.map((i) => i.itemId);
      // Purge les ids « vus » qui ne sont plus possédés (set borné dans le temps).
      const owned = new Set(itemIds.value);
      let changed = false;
      for (const id of [...seen.value]) {
        if (!owned.has(id)) { seen.value.delete(id); changed = true; }
      }
      if (changed) { seen.value = new Set(seen.value); persist(); }
    } catch { /* silencieux : pas de badge plutôt qu'une erreur */ }
  }

  function clear(): void {
    itemIds.value = [];
  }

  return { itemIds, newIds, newCount, isNew, markSeen, markAllSeen, refresh, clear };
});
